// src/backupHeartbeat.js
// Emits "online"/"offline" (and "app:online"/"app:offline") on EVERY tick.
// CRA4-friendly (no ||=, no globalThis). Singleton poller that runs for the entire down cycle.

const DEFAULT_TIMEOUT_MS = 2500;

const env = import.meta.env;

// Optional override: VITE_HEARTBEAT_URLS="https://your-host/_hb,https://clients3.google.com/generate_204"
const ENV_URLS = (env.VITE_HEARTBEAT_URLS || "")
  .split(",")
  .map(function (s) {
    return s.trim();
  })
  .filter(Boolean);

// Reliable public endpoints; success = network reachable.
const FALLBACK_URLS = [
  "https://clients3.google.com/generate_204",
  "https://cloudflare.com/cdn-cgi/trace",
];

const HEARTBEAT_URLS = ENV_URLS.length ? ENV_URLS : FALLBACK_URLS;

function withTimeout(ms) {
  var ctrl = new AbortController();
  var t = setTimeout(function () {
    try {
      ctrl.abort();
    } catch (_) {
      // The timeout cleanup is best effort.
    }
  }, ms);
  return {
    run: function (fn) {
      return fn(ctrl.signal).finally(function () {
        clearTimeout(t);
      });
    },
  };
}

async function pingOnce(url, timeoutMs) {
  var bust = url.indexOf("?") >= 0 ? "&ts=" + Date.now() : "?ts=" + Date.now();
  var runner = withTimeout(timeoutMs || DEFAULT_TIMEOUT_MS);
  return runner.run(function (signal) {
    return fetch(url + bust, {
      method: "GET",
      mode: "no-cors",
      cache: "no-store",
      redirect: "follow",
      signal: signal,
    });
  });
}

// Raw heartbeat: try URLs sequentially; succeed fast
export async function secondaryHeartbeat(timeoutPerUrlMs) {
  for (var i = 0; i < HEARTBEAT_URLS.length; i++) {
    try {
      await pingOnce(HEARTBEAT_URLS[i], timeoutPerUrlMs);
      return true;
    } catch (e) {
      // try next URL
    }
  }
  return false;
}

/** Throttled wrapper: reuse recent result / in-flight promise */
var _hbInflight = null;
var _hbLastCompletedTs = 0;
var _hbLastResult = false;

export function secondaryHeartbeatThrottled(minMs) {
  if (typeof minMs !== "number") minMs = 4000;
  var now = Date.now();

  if (_hbInflight) return _hbInflight;
  if (now - _hbLastCompletedTs < minMs) return Promise.resolve(_hbLastResult);

  _hbInflight = secondaryHeartbeat()
    .then(function (ok) {
      _hbLastResult = !!ok;
      _hbLastCompletedTs = Date.now();
      _hbInflight = null;
      return _hbLastResult;
    })
    .catch(function () {
      _hbLastResult = false;
      _hbLastCompletedTs = Date.now();
      _hbInflight = null;
      return false;
    });

  return _hbInflight;
}

/** Event emitter helper: native + namespaced custom */
function emitConnectivity(ok, source) {
  try {
    var evName = ok ? "online" : "offline";
    window.dispatchEvent(new Event(evName));
    var custom = "app:" + evName; // "app:online" / "app:offline"
    try {
      window.dispatchEvent(
        new CustomEvent(custom, { detail: { source: source || "heartbeat" } })
      );
    } catch (_) {
      window.dispatchEvent(new Event(custom));
    }
  } catch (_) {
    // Event dispatch is best effort.
  }
}

/** ===== Singleton poller (HMR-safe) ===== */
// Use window; avoid globalThis for CRA4
var G = typeof window !== "undefined" ? window : {};
var KEY = "__VALCOUR_HEARTBEAT_POLL__";

// Global state survives module reloads; guarantees one poller per tab
if (!G[KEY]) {
  G[KEY] = { id: null, first: null, inFlight: false };
}

export function startSecondaryPoll(intervalMs, onOnline) {
  if (typeof intervalMs !== "number") intervalMs = 5000;
  var S = G[KEY];
  if (S.id) return; // already running

  var jitter = Math.floor(Math.random() * 500); // 0-500ms
  var tick = async function () {
    if (S.inFlight) return;
    S.inFlight = true;
    try {
      var ok = await secondaryHeartbeatThrottled(4000);

      // ✅ Emit EVERY tick (no transition gating)
      emitConnectivity(ok, "poller");

      // If we have internet, let caller try reconnecting the socket.
      if (ok && typeof onOnline === "function") {
        try {
          onOnline();
        } catch (_) {
          // Keep polling if the callback fails.
        }
      }

      // Keep running until socket.js calls stopSecondaryPoll() on real reconnect
    } finally {
      S.inFlight = false;
    }
  };

  S.first = setTimeout(tick, jitter); // quick first check with jitter
  S.id = setInterval(tick, Math.max(2000, intervalMs)); // continue until explicitly stopped
}

export function stopSecondaryPoll() {
  var S = G[KEY];
  if (!S.id) return;
  clearInterval(S.id);
  if (S.first) clearTimeout(S.first);
  S.id = null;
  S.first = null;
  S.inFlight = false;
}

// Clean up on hot-reload so old intervals don’t leak
if (typeof module !== "undefined" && module.hot) {
  module.hot.dispose(function () {
    stopSecondaryPoll();
  });
}
